import { MessageSquare, Send, Square } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import { useChat, type ChatStatus } from './useChat';

import { type ChatMessage } from '@/api/chat';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { CardPreview } from '@/domain/CardPreview';
import { cn } from '@/lib/utils';




const meta: WidgetMeta = {
  id: 'chat',
  name: 'Chat with Ugin',
  // Not "persistent across sessions" any more: OpenClaw kept the conversation
  // server-side, and this one lives in the tab. Saying otherwise invites
  // someone to close it expecting to come back to it.
  description: 'Grounded in your own games. This conversation lives in this tab.',
  icon: MessageSquare,
  defaultSize: { w: 12, h: 8 },
  category: 'system',
};

export interface ChatWidgetProps {
  initialPrompt?: string;
  fullPage?: boolean;
}

export const ChatWidget: WidgetComponent<ChatWidgetProps> = ({ initialPrompt, fullPage = false }) => {
  const { messages, streaming, status, error, send, stop } = useChat();
  const [draft, setDraft] = useState(initialPrompt ?? '');
  const scrollEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, streaming?.text]);

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    send(draft);
    setDraft('');
  };

  return (
    <WidgetContainer
      meta={meta}
      icon={<MessageSquare className="size-4" />}
      actions={<StatusBadge status={status} />}
      className={cn(fullPage ? 'h-[calc(100vh-9rem)]' : 'min-h-[480px]')}
      contentClassName="flex flex-col gap-3 p-0"
    >
      {error ? (
        <div role="alert" className="mx-6 mb-2 rounded-md border border-ugin-danger/40 bg-ugin-danger/10 px-3 py-2 text-xs text-ugin-danger">
          {error.message}
        </div>
      ) : null}
      <ScrollArea className="flex-1 px-6">
        <ul className="space-y-4 py-3" aria-live="polite">
          {messages.map((m) => (
            <ChatBubble key={m.id} message={m} />
          ))}
          {streaming ? (
            <ChatBubble
              message={{
                id: streaming.id,
                role: 'assistant',
                content: streaming.text + '▍',
                createdAt: new Date().toISOString(),
              }}
              streaming
            />
          ) : null}
          <div ref={scrollEndRef} />
        </ul>
      </ScrollArea>
      <form
        onSubmit={handleSubmit}
        className="flex items-end gap-2 border-t border-ugin-border/40 bg-ugin-bg-deep/30 px-6 py-3"
      >
        <Textarea
          value={draft}
          onChange={(e) => { setDraft(e.target.value); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          placeholder="Ask Ugin about the meta, a deck, a matchup…"
          className="min-h-[40px] max-h-40 resize-none font-sans"
          aria-label="Message to Ugin"
        />
        {status === 'thinking' ? (
          <Button type="button" variant="secondary" onClick={stop}>
            <Square className="size-4" />
            <span className="sr-only">Stop</span>
          </Button>
        ) : (
          <Button type="submit" disabled={!draft.trim()}>
            <Send className="size-4" />
            <span className="sr-only">Send</span>
          </Button>
        )}
      </form>
    </WidgetContainer>
  );
};

ChatWidget.meta = meta;

function ChatBubble({ message, streaming }: { message: ChatMessage; streaming?: boolean }) {
  const isUser = message.role === 'user';
  return (
    <motion.li
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className={cn('flex gap-3', isUser ? 'flex-row-reverse' : 'flex-row')}
    >
      <div
        aria-hidden="true"
        className={cn(
          'mt-1 size-7 shrink-0 rounded-full grid place-items-center text-xs font-display',
          isUser
            ? 'bg-ugin-surface-elevated text-ugin-text-muted'
            : 'bg-gradient-to-br from-ugin-accent to-ugin-mystic text-ugin-bg-deep',
        )}
      >
        {isUser ? 'R' : '🐉'}
      </div>
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'bg-ugin-surface-elevated text-ugin-text'
            : 'bg-ugin-surface/60 text-ugin-text border border-ugin-border/40',
        )}
      >
        <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-pre:bg-ugin-bg-deep prose-pre:border prose-pre:border-ugin-border">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={
              isUser
                ? undefined
                : {
                    a: ({ children, href }) =>
                      href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                        </a>
                      ) : (
                        <span>{children}</span>
                      ),
                    code: ({ children, ...props }) =>
                      typeof children === 'string' && children.length < 60 ? (
                        <CardCodeReplacement value={children} />
                      ) : (
                        <code {...props}>{children}</code>
                      ),
                  }
            }
          >
            {message.content}
          </ReactMarkdown>
        </div>
        {streaming ? <span className="sr-only">Ugin is typing</span> : null}
      </div>
    </motion.li>
  );
}

function CardCodeReplacement({ value }: { value: string }) {
  // Backtick-wrapped card name in Ugin's output → CardPreview hover.
  return <CardPreview name={value.trim()} />;
}

/**
 * A turn's state, not a socket's. There is no connection to be connected to:
 * the previous version reported "Connected" from a hardcoded true, directly
 * above an error saying no gateway token was configured.
 */
function StatusBadge({ status }: { status: ChatStatus }) {
  const label =
    status === 'thinking' ? 'Thinking…' : status === 'error' ? 'Unavailable' : 'Ready';
  const dot =
    status === 'thinking' ? 'bg-ugin-warn animate-pulse' :
    status === 'error' ? 'bg-ugin-danger' :
    'bg-ugin-success';
  return (
    <div className="flex items-center gap-2 text-xs text-ugin-text-muted">
      <span className={cn('size-1.5 rounded-full', dot)} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}
